import { store } from '@/stores/store';

export type ChatCompletionMessage = {
  role: 'system' | 'user' | 'assistant';
  content: string;
};

export type ChatRefAssetItem = {
  assetId: string;
  role?: 'reference_image';
};

export type StreamChatOptions = {
  messages: ChatCompletionMessage[];
  model: string;
  personaOwnerUserId?: string;
  refAssetIds?: ChatRefAssetItem[];
  signal?: AbortSignal;
  onChunk: (text: string) => void;
};

function buildAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) headers.Authorization = `Bearer ${token}`;

  const state = store.getState() as { auth?: { user?: { spaces?: Array<{ id: string }> } } };
  const spaceId = state.auth?.user?.spaces?.[0]?.id;
  if (spaceId) headers['x-space-id'] = spaceId;

  return headers;
}

export async function streamChatCompletion(options: StreamChatOptions): Promise<string> {
  const res = await fetch('/api/ai_chat/completions/stream', {
    method: 'POST',
    headers: buildAuthHeaders(),
    credentials: 'include',
    signal: options.signal,
    body: JSON.stringify({
      messages: options.messages,
      model: options.model,
      ...(options.personaOwnerUserId
        ? { personaOwnerUserId: options.personaOwnerUserId }
        : {}),
      ...(options.refAssetIds?.length ? { refAssetIds: options.refAssetIds } : {}),
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `AI 请求失败（HTTP ${res.status}）`);
  }

  if (!res.body) {
    throw new Error('流式响应不可用');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';
  let pendingEvent: string | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        pendingEvent = null;
        continue;
      }

      if (trimmed.startsWith('event:')) {
        pendingEvent = trimmed.slice(6).trim();
        continue;
      }

      if (!trimmed.startsWith('data:')) continue;
      const payload = trimmed.slice(5).trim();
      if (!payload || payload === '[DONE]') continue;

      let parsed: { t?: string; message?: string };
      try {
        parsed = JSON.parse(payload) as { t?: string; message?: string };
      } catch {
        continue;
      }

      if (pendingEvent === 'error' || parsed.message) {
        throw new Error(parsed.message || 'AI 流式响应出错');
      }

      if (typeof parsed.t === 'string' && parsed.t) {
        full += parsed.t;
        options.onChunk(full);
      }
    }
  }

  if (!full.trim()) {
    throw new Error('模型未返回有效内容，请重试或更换模型');
  }

  return full;
}
